// Who is physically holding a remittance's cash — 0166.
//
// ⚠ ITS OWN FILE, AND NOT `cash.hooks.ts`, FOR A DULL BUT REAL REASON: this is a
// pure function over a row, and the hooks file imports the Supabase client and
// the Edge-Function caller. Sitting next to those made it untestable — the
// admin vitest setup does not resolve the app's `@/` alias, so importing the
// label dragged in a module the test runner could not load.

/**
 * Who physically has the money right now — 0166.
 *
 * ⚠ `pending` STOPPED MEANING ONE THING when the hand-over shipped. It used to
 * be "the therapist has it and nobody has taken it"; it now also covers "she
 * handed it to a rider who is bringing it in". Both are pre-hub, so the stage
 * ladder is unchanged and correct — but a hub manager looking at this table
 * needs to know which of the two a row is, because only one of them is waiting
 * on somebody in the building.
 *
 * Returns a plain sentence rather than a chip: the stage column already carries
 * the badge, and a second coloured pill next to it would read as a second
 * status.
 */
export function custodyLabel(row: {
  status: string | null;
  recipient_kind: string | null;
  transferred_at: string | null;
  rider_confirmed_at: string | null;
  therapist?: { full_name: string } | null;
  recipient?: { full_name: string } | null;
  receiver?: { full_name: string } | null;
  collector?: { full_name: string } | null;
}): string {
  const therapist = row.therapist?.full_name ?? "the therapist";
  const rider = row.recipient?.full_name ?? "the assigned rider";

  if (row.status === "collected") {
    return row.collector?.full_name ? `Main office · ${row.collector.full_name}` : "Main office";
  }
  if (row.status === "received") {
    return row.receiver?.full_name ? `Hub · ${row.receiver.full_name}` : "Hub";
  }
  // Everything below is `pending`, and the columns are what tell them apart.
  if (row.rider_confirmed_at) return `${rider} — carrying it in`;
  if (row.transferred_at) return `${therapist} → ${rider}, awaiting his confirmation`;
  if (row.recipient_kind === "rider") return `${therapist} — to hand to ${rider}`;
  if (row.recipient_kind === "hub") return `${therapist} — to remit at the hub`;
  return `${therapist} — no recipient selected yet`;
}

/**
 * Is this row cash somebody is actually waiting on? — 0203/0207.
 *
 * ⚠ A ₱0 REMITTANCE IS A REAL RECORD AND NOT A REAL TASK, and admin is the one
 * role that needs both readings. Since `0203` every cash booking opens a
 * remittance, including at ₱0 — when the Therapist was owed at least as much as
 * she collected, she keeps all of it and nothing travels onward. CASH requires
 * the process to have happened (*"Every booking paid by Cash always has a Cash
 * Handling process"*) and the row is that evidence, so the remittance TABLE
 * keeps showing it: *"Admin and Hub Manager may see the complete Cash Handling
 * amount, status, confirmations, issue report, balance update, and audit
 * information."*
 *
 * What it must never do is count as outstanding work. `instruct_cash_settlement`
 * deliberately never instructs a ₱0 row, so it stays `pending` for ever with no
 * recipient — and every count, badge, queue and alert built on `status ===
 * 'pending'` treated that as cash a hub was still waiting for. It cannot be
 * received, because there is nothing to hand over.
 *
 * ⚠ THE SAME CALL `v_cash_custody` MAKES, DELIBERATELY. `0207` filters ₱0 out of
 * that view on the reasoning that nobody is holding nothing; this is that
 * sentence applied to the admin surfaces, which read `cash_remittances` directly
 * and so never passed through the view.
 */
export function isOutstandingCash(row: { status: string | null; amount: number }): boolean {
  return row.status === "pending" && row.amount > 0;
}
